import type {PaymentProvider} from "../../api/models";

// Buying a license: the checkout form, the page that opens Paddle's checkout, and the page the providers send the
// customer back to.
export const checkout = {
    product: 'Product',
    buy: 'Buy',
    title: 'Buy {product}',
    notOnSale: "{product} isn't on sale online right now.",
    cancelled: "The payment was canceled, and you haven't been charged. You can try again below.",
    notForSale: "{product} isn't on sale right now.",
    tooManyStarted: "You've started a lot of purchases in the last hour. Please try again later.",
    // Under the product's name.
    edition: '{edition} edition, perpetual license with free updates until the next major version',
    // Signed out: a guest's purchase makes their account (or adds the license to the one with this address).
    email: 'Email address',
    emailHelp: "Your license and receipt go here. We'll create your account for it, or add the license to the one that has it. Already have an account? <link>Sign in</link>",
    licensee: 'Licensed to',
    licenseeHelp: "Shown in {product}'s license dialog: your name, or your company's.",
    payWith: 'Pay with',
    providers: {
        stripe: 'Credit or debit card',
        paypal: 'PayPal',
        paddle: 'Card, PayPal and more'
    } satisfies Record<PaymentProvider, string>,
    taxDepends: 'Sales tax or VAT depends on where you are.',
    country: 'Country',
    chooseCountry: 'Choose your country',
    postalCodeRequired: 'ZIP or postal code',
    postalCodeOptional: 'Postal code (optional)',
    price: 'Price',
    tax: 'Tax',
    total: 'Total',
    taxOnNextPage: 'Any sales tax or VAT is added on the next page, from your billing address.',
    // Also the age the terms require: buying is how accounts are made, so this is where it's confirmed.
    agree: "I agree to the <link>terms of use</link>, including the {product} license terms, and the <refunds>refund policy</refunds>, and I'm at least 16.",
    // EU and UK consumers' right to cancel a digital purchase within 14 days, given up on delivery.
    waive: 'I want my license straight away, and I understand that once I download or activate {product} I lose any right to cancel the purchase.',
    continueToPayPal: 'Continue to PayPal',
    continueToPayment: 'Continue to payment',
    securePayPal: "You'll pay on PayPal's secure page. Your card and account details go to them, never to us.",
    secureStripe: "You'll pay on Stripe's secure page. Your card and account details go to them, never to us.",
    // Paddle is the merchant of record: it sells the license, so its name is on the receipt and the statement.
    securePaddle: "You'll pay on the secure checkout of Paddle, our online reseller, which handles the order, any sales tax or VAT, and your receipt. Paddle.com will appear on your statement. Your card and account details go to Paddle, never to us.",
    problems: {
        email: 'Enter your email address.',
        licensee: 'Enter who the license is for.',
        country: 'Choose your country.',
        postalCode: 'Enter your ZIP or postal code.',
        agreed: 'You need to agree to the terms, and be at least 16, to buy a license.',
        waived: 'You need to agree to this to get the license straight away.'
    },

    // /checkout/pay: Paddle's checkout opens over it.
    pay: {
        title: 'Checkout',
        opening: 'Opening the secure checkout…',
        // <link> goes back to the checkout form.
        notOpening: "If it doesn't open, <link>go back</link> and try again.",
        failed: "The checkout couldn't be opened, and you haven't been charged. Please <link>go back</link> and try again."
    },

    // /checkout/complete
    complete: {
        checkout: 'Checkout',
        noOrder: "There's no order to show. <link>Your orders</link>",
        orderNotFound: "That order couldn't be found.",
        finishing: 'Finishing your purchase',
        // Read out by screen readers.
        announceReady: 'Payment complete. Your license is ready.',
        thankYou: 'Thank you',
        ready: "Your {product} license is ready. We've also emailed it to you, with your receipt.",
        // A guest's purchase: the account made for them, or the one that already had the address.
        accountCreated: "We've created your account for {email}. Check your email for a link to choose its password.",
        accountExisting: 'The license has been added to your account for <email>{email}</email>. Sign in to see it with your others.',
        licenseKey: 'License key',
        copyLicenseKey: 'Copy license key',
        download: 'Download {product}',
        myLicenses: 'My licenses',
        processing: 'Payment processing',
        waitingPayPal: 'Waiting for PayPal to confirm…',
        waitingPayment: 'Waiting for the payment to confirm…',
        stillProcessing: "Your payment is still being processed. Some payment methods take a few days to clear. We'll email your license as soon as it does, and it'll appear in <link>My licenses</link>.",
        declined: 'Payment declined',
        // <link> goes back to the checkout.
        declinedText: "The payment didn't go through, and you haven't been charged. You can <link>try again</link> with another card or account.",
        expired: 'Checkout expired',
        expiredText: 'This checkout was never paid for, and has expired. You can <link>try again</link>.',
        summary: {
            order: 'Order',
            product: 'Product',
            productValue: '{product}, {edition} edition',
            licensee: 'Licensed to',
            total: 'Total',
            includingTax: ' (including {tax} tax)'
        }
    }
};

export type CheckoutMessages = typeof checkout;
